import React, { useState, useEffect, useRef, useCallback } from 'react'
import classnames from 'classnames'
import { Route } from 'react-router-dom'
import FocusTrap from 'focus-trap-react'
import { FormattedMessage } from 'react-intl'
import {
  getDocumentTitle,
  setDocumentTitle,
  getCurrentPath,
  scrollToElement
} from 'src/helpers/browser'
import BodyScrollLock from 'src/Components/BodyScrollLock'
import Header from 'src/Components/Header'
import Panel from 'src/Components/Panel'
import { Button } from 'src/Components'
import { IconAngleLeft } from 'src/Components/Icon'

import styles from './OverlayPanel.module.scss'

const OverlayRouter = ({ title, path, onOpen, onClose, ...restProps }) => {
  const fromTitle = useRef(getDocumentTitle())
  const [direct, setDirect] = useState(getCurrentPath().includes(path))
  const [open, setOpen] = useState(false)

  const doOnOpen = useCallback(() => {
    setOpen(true)
    typeof onOpen === 'function' && onOpen()
  }, [onOpen])

  const doOnClose = useCallback(() => {
    setOpen(false)
    setDirect(false)
    typeof onClose === 'function' && onClose()
  }, [onClose])

  useEffect(() => {
    const resetTitle = fromTitle.current
    if (open) {
      setDocumentTitle(`Homebot - ${title}`)
    } else {
      setDocumentTitle(resetTitle)
    }
  }, [open, title])

  return (
    <Route
      path={path}
      render={args => (
        <OverlayInner
          onOpen={doOnOpen}
          onClose={doOnClose}
          args={args}
          direct={direct}
          title={title}
          {...restProps}
        />
      )}
    />
  )
}

const OverlayInner = ({
  component: Component,
  render,
  args,
  title,
  back,
  direct,
  anchorOffset,
  className,
  onOpen,
  onClose
}) => {
  const anchor = useRef(null)
  const backButton = useRef(null)

  const doNavBack = useCallback(
    history => {
      if (direct && back) {
        history.push(back)
      } else {
        history.goBack()
      }
    },
    [back, direct]
  )

  useEffect(() => {
    onOpen()
    return () => onClose()
  }, [onOpen, onClose])

  useEffect(() => {
    if (direct) {
      const element = anchor.current
      scrollToElement(element)
      return () => scrollToElement(element, { padding: anchorOffset || 0 })
    }
  }, [direct, anchorOffset])

  document.documentElement.style.width = '100vw'
  document.documentElement.style.height = '100vh'
  document.documentElement.style.overflow = 'hidden'
  useEffect(
    () => () => {
      document.documentElement.style.removeProperty('width')
      document.documentElement.style.removeProperty('height')
      document.documentElement.style.removeProperty('overflow')
    },
    []
  )

  return (
    <div ref={anchor} className={styles.overlayAnchor}>
      <FocusTrap
        focusTrapOptions={{
          onActive: setTimeout(
            () => document.activeElement && document.activeElement.blur()
          )
        }}
      >
        <BodyScrollLock allowTouchMove>
          <div className={styles.overlayCurtain}>
            <Header className={styles.headerBar}>
              <Button
                size='xl'
                className={styles.backButton}
                onClick={() => doNavBack(args.history)}
                ref={backButton}
              >
                <span className={styles.buttonInner}>
                  <IconAngleLeft className={styles.icon} />
                  <span className={styles.backButtonText}>
                    <FormattedMessage id='Button.Back' defaultMessage='Back' />
                  </span>
                </span>
              </Button>
              <div>{title}</div>
            </Header>
            <Panel className={classnames(styles.overlayPanel, className)}>
              {Component && <Component {...args} />}
              {!Component && render && render(args)}
            </Panel>
          </div>
        </BodyScrollLock>
      </FocusTrap>
    </div>
  )
}

export default OverlayRouter
